import React, { useState, useEffect, useCallback } from 'react';
import { Typography, Spin, Button, Empty, Timeline, Radio, Space, Badge } from 'antd';
import { RocketOutlined, ReloadOutlined, CheckOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

// TODO: 联调时替换为接口数据
const MOCK_ALL = [
  { key: '1', title: 'test-设备分组管理上线', publishTime: '2026-07-01 10:00', content: '## 新功能\n\n- 支持按产品/区域/标签灵活分组\n- 分组后可批量执行固件升级和配置下发\n\n> 本次更新预计在 7 月 5 日前全量推送', visibleTo: ['enterprise', 'platform'] },
  { key: '2', title: 'test-消费账户余额监控报警优化', publishTime: '2026-06-28 14:30', content: '## 优化\n\n- 报警阈值支持自定义\n- 新增短信提醒频率控制\n- 修复部分企业未收到提醒的问题', visibleTo: ['enterprise'] },
  { key: '3', title: 'test-信用额度审批流程优化', publishTime: '2026-06-25 09:00', content: '## 平台内部更新\n\n- 信用额度审批支持批量操作\n- 审批流程节点简化', visibleTo: ['platform'] },
  { key: '4', title: 'test-套餐底价扣除配置上线', publishTime: '2026-06-20 09:00', content: '## 新功能\n\n### 企业商务配置\n平台客户企业详情新增**套餐底价扣除配置**：\n1. 支持设置扣除打折比例\n2. 支持按 App 应用组单独配置', visibleTo: ['enterprise'] },
  { key: '5', title: 'test-佣金抽成规则调整', publishTime: '2026-06-15 16:00', content: '## 平台内部更新\n\n- 抽佣比例上限调整\n- 新增阶梯抽佣模式', visibleTo: ['platform'] },
  { key: '6', title: 'test-固件 OTA 推送优化', publishTime: '2026-06-10 11:00', content: '## 优化\n\n- 大文件分片推送\n- 推送进度实时展示\n- 失败自动重试 3 次', visibleTo: ['enterprise', 'platform'] },
  { key: '7', title: 'test-生产授权直充功能上线', publishTime: '2026-06-05 15:00', content: '## 新功能\n\n### 授权直充\n平台运营可直接为企业增加生产授权余额\n\n### 授权退款\n支持操作失误后的授权回退', visibleTo: ['platform', 'enterprise'] },
  { key: '8', title: 'test-API 接口文档更新', publishTime: '2026-05-28 10:00', content: '## 文档更新\n\n- 新增设备管理接口\n- 更新认证鉴权说明', visibleTo: ['enterprise'] },
];

const PAGE_SIZE = 5;

const STORAGE_KEY = 'announcement_read';

// 获取已读状态：{ role_announcementKey: true }
function getReadMap() {
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || '{}');
  } catch { return {}; }
}

// Mock 账号（联调时替换为真实 accountId）
function getMockAccountId() {
  try {
    return JSON.parse(localStorage.getItem('user_info') || '{}')?.user_name || 'mock_user';
  } catch { return 'mock_user'; }
}

function getReadKey(role, announcementKey) {
  return `${getMockAccountId()}_${role}_${announcementKey}`;
}

function setRead(role, announcementKey) {
  const map = getReadMap();
  map[getReadKey(role, announcementKey)] = true;
  localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
  // 触发 Badge 更新
  window.dispatchEvent(new Event('badge_update'));
}

function isRead(role, announcementKey) {
  return !!getReadMap()[getReadKey(role, announcementKey)];
}

// 获取当前账号未读数量
export function getUnreadCount(role) {
  const map = getReadMap();
  return MOCK_ALL.filter(a => a.visibleTo.includes(role)).filter(a => !map[getReadKey(role, a.key)]).length;
}

// 简易 Markdown → HTML
function simpleMarkdownToHtml(md) {
  if (!md) return '';
  let html = md
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h4 style="margin:8px 0 4px;font-size:15px">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:12px 0 6px;font-size:17px">$1</h3>')
    .replace(/^# (.+)$/gm, '<h2 style="margin:16px 0 8px;font-size:20px">$1</h2>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/`(.+?)`/g, '<code style="background:#f5f5f5;padding:2px 6px;border-radius:3px;font-size:13px">$1</code>')
    .replace(/^> (.+)$/gm, '<blockquote style="border-left:3px solid #d0d5dd;margin:8px 0;padding:4px 12px;color:#667085">$1</blockquote>')
    .replace(/^- (.+)$/gm, '<li>$1</li>')
    .replace(/^(\d+)\. (.+)$/gm, '<li>$2</li>')
    .replace(/((?:<li>.*<\/li>\n?)+)/g, '<ul style="margin:4px 0;padding-left:20px">$1</ul>')
    .replace(/\[(.+?)\]\((.+?)\)/g, '<a href="$2" target="_blank" style="color:#1677ff">$1</a>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/\n/g, '<br/>');
  return html;
}

const TimelinePage = () => {
  const [mockRole, setMockRole] = useState('enterprise'); // 'platform' | 'enterprise'
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [, setReadMap] = useState(getReadMap);
  // 刷新计数器，用于强制重渲染
  const [refreshKey, setRefreshKey] = useState(0);

  // 过滤当前角色可见的公告
  const visibleList = MOCK_ALL.filter(a => a.visibleTo.includes(mockRole));

  // 初始加载
  useEffect(() => {
    setLoading(true);
    setTimeout(() => {
      setItems(visibleList.slice(0, PAGE_SIZE));
      setHasMore(visibleList.length > PAGE_SIZE);
      setLoading(false);
      // 初始化未读计数到 localStorage，供 MainLayout Badge 读取
      const unread = getUnreadCount(mockRole);
      localStorage.setItem(`unread_count_${mockRole}`, String(unread));
      window.dispatchEvent(new Event('badge_update'));
    }, 300);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mockRole, refreshKey]);

  // 加载更多
  const loadMore = useCallback(() => {
    if (loadingMore || !hasMore) return;
    setLoadingMore(true);
    setLoadError(false);
    setTimeout(() => {
      const nextPage = page + 1;
      const start = page * PAGE_SIZE;
      const end = start + PAGE_SIZE;
      const newItems = visibleList.slice(start, end);
      if (newItems.length === 0) {
        setHasMore(false);
      } else {
        setItems(prev => [...prev, ...newItems]);
        setPage(nextPage);
        setHasMore(visibleList.length > end);
      }
      setLoadingMore(false);
    }, 300);
  }, [page, loadingMore, hasMore, visibleList]);

  // 标记已读
  const handleMarkRead = (record) => {
    setRead(mockRole, record.key);
    setReadMap(getReadMap());
    setRefreshKey(k => k + 1);
  };

  // 全部已读
  const handleMarkAllRead = () => {
    const map = getReadMap();
    visibleList.forEach(a => {
      map[getReadKey(mockRole, a.key)] = true;
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(map));
    setReadMap({ ...map });
    setRefreshKey(k => k + 1);
    window.dispatchEvent(new Event('badge_update'));
  };

  // 滚动监听
  const handleScroll = useCallback((e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    if (scrollHeight - scrollTop - clientHeight < 100) {
      loadMore();
    }
  }, [loadMore]);

  // Timeline items
  const timelineItems = items.map((item, idx) => {
    const read = isRead(mockRole, item.key);
    return {
      key: item.key,
      dot: idx === 0 && !read ? <RocketOutlined style={{ fontSize: 16, color: '#1677ff' }} /> : undefined,
      color: read ? 'gray' : 'blue',
      children: (
        <div style={{ marginBottom: 4 }}>
          <Text type="secondary" style={{ fontSize: 12 }}>{item.publishTime}</Text>
          <div style={{
            background: '#fafbfc',
            border: `1px solid ${read ? '#f0f0f0' : '#1677ff'}`,
            borderLeft: `4px solid ${read ? '#d9d9d9' : '#1677ff'}`,
            borderRadius: 8,
            padding: '16px 20px',
            marginTop: 6,
            position: 'relative',
          }}>
            {!read && (
              <Button
                type="link"
                size="small"
                icon={<CheckOutlined />}
                onClick={(e) => { e.stopPropagation(); handleMarkRead(item); }}
                style={{ position: 'absolute', top: 12, right: 12, fontSize: 12 }}
              >
                标记已读
              </Button>
            )}
            <div style={{ fontSize: 15, fontWeight: read ? 400 : 600, marginBottom: 8, color: '#1a1a1a', paddingRight: read ? 0 : 90 }}>
              {item.title}
            </div>
            <div
              style={{ fontSize: 14, lineHeight: 1.8, color: '#555' }}
              dangerouslySetInnerHTML={{ __html: simpleMarkdownToHtml(item.content) }}
            />
          </div>
        </div>
      ),
    };
  });

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <Spin size="large" tip="加载中..." />
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400, flexDirection: 'column', gap: 16 }}>
        <Empty description="暂无更新日志" />
        <Space>
          <Text type="secondary">Mock角色：</Text>
          <Radio.Group value={mockRole} onChange={e => { setMockRole(e.target.value); setPage(1); }} optionType="button" buttonStyle="solid" size="small">
            <Radio.Button value="platform">平台用户</Radio.Button>
            <Radio.Button value="enterprise">企业用户</Radio.Button>
          </Radio.Group>
        </Space>
      </div>
    );
  }

  const unreadCount = getUnreadCount(mockRole);

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '32px 24px' }}>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ textAlign: 'center', flex: 1 }}>
          <RocketOutlined style={{ fontSize: 28, color: '#1677ff', marginRight: 12 }} />
          <Title level={3} style={{ display: 'inline', margin: 0, verticalAlign: 'middle' }}>更新日志</Title>
          {unreadCount > 0 && (
            <Badge count={unreadCount} overflowCount={99} style={{ marginLeft: 12 }} />
          )}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {unreadCount > 0 && (
            <Button size="small" onClick={handleMarkAllRead}>全部已读</Button>
          )}
          <Space>
          <Text type="secondary" style={{ fontSize: 12 }}>Mock：</Text>
          <Radio.Group value={mockRole} onChange={e => { setMockRole(e.target.value); setPage(1); }} optionType="button" buttonStyle="solid" size="small">
            <Radio.Button value="platform">平台用户</Radio.Button>
            <Radio.Button value="enterprise">企业用户</Radio.Button>
          </Radio.Group>
        </Space>
        </div>
      </div>

      <div
        style={{ maxHeight: 'calc(100vh - 200px)', overflowY: 'auto', paddingRight: 8 }}
        onScroll={handleScroll}
      >
        <Timeline items={timelineItems} />

        <div style={{ textAlign: 'center', padding: '20px 0' }}>
          {loadingMore && <Spin tip="加载中..." />}
          {!loadingMore && !hasMore && items.length > 0 && (
            <Text type="secondary" style={{ fontSize: 13 }}>— 已加载全部更新 —</Text>
          )}
          {loadError && (
            <Button icon={<ReloadOutlined />} onClick={() => { setLoadError(false); loadMore(); }}>加载失败，点击重试</Button>
          )}
        </div>
      </div>
    </div>
  );
};

export default TimelinePage;
